import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiExternalLink, FiFileText, FiLink2, FiMail } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';

function WaitList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0 });
  const [subEmail, setSubEmail] = useState('');
  const [subFirstName, setSubFirstName] = useState('');
  const [subLastName, setSubLastName] = useState('');
  const [subLoading, setSubLoading] = useState(false);
  const [subDone, setSubDone] = useState(false);

  useEffect(() => {
    loadPosts();
  }, []);

  useEffect(() => {
    if (posts.length === 0) return;
    const hash = window.location.hash;
    if (hash.startsWith('#post-')) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('link-highlight');
          setTimeout(() => el.classList.remove('link-highlight'), 2000);
        }, 600);
      }
    }
  }, [posts]);

  const loadPosts = async () => {
    try {
      const response = await fetch('/api/posts');
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setPosts(data);
    } catch (error) {
      console.error('Error loading posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubLoading(true);
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: subEmail, name: [subFirstName, subLastName].filter(Boolean).join(' ') }),
      });
      if (res.ok) {
        setSubDone(true);
        setSubEmail('');
        setSubFirstName('');
        setSubLastName('');
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to subscribe');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setSubLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'Z').toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-16 h-16 border-4 border-black dark:border-white border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-20">
      <div ref={ref} className="max-w-2xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="mb-12"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-3 gradient-text">
            Wait List
          </h1>
          <p className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
            My thoughts on the Internet.
          </p>
        </motion.div>

        {/* Subscribe form */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1 }}
          className="card-modern p-6 mb-10"
        >
          {subDone ? (
            <div className="flex items-center space-x-2">
              <FiMail className="text-gray-500" />
              <span className="font-semibold text-gray-900 dark:text-white">Successfully subscribed.</span>
            </div>
          ) : (
            <>
              <div className="flex items-center space-x-2 mb-3">
                <FiMail className="text-gray-500" />
                <span className="font-semibold text-gray-900 dark:text-white">Get weekly recaps in your inbox</span>
              </div>
              <form onSubmit={handleSubscribe} className="flex flex-col gap-3">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={subFirstName}
                    onChange={(e) => setSubFirstName(e.target.value)}
                    placeholder="First name"
                    autoComplete="given-name"
                    className="flex-1 min-w-0 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                  />
                  <input
                    type="text"
                    value={subLastName}
                    onChange={(e) => setSubLastName(e.target.value)}
                    placeholder="Last name"
                    autoComplete="family-name"
                    className="flex-1 min-w-0 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                  />
                </div>
                <div className="flex gap-3 flex-wrap">
                  <input
                    type="email"
                    value={subEmail}
                    onChange={(e) => setSubEmail(e.target.value)}
                    placeholder="your@email.com"
                    autoComplete="email"
                    required
                    className="flex-1 min-w-0 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                  />
                  <button
                    type="submit"
                    disabled={subLoading}
                    className="btn-modern px-6 py-2 disabled:opacity-50"
                  >
                    {subLoading ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
              </form>
            </>
          )}
        </motion.div>

        {posts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            className="text-center py-20"
          >
            <div className="inline-block p-6 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
              <FiFileText className="w-16 h-16 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              No posts yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Check back soon for updates and announcements!
            </p>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {posts.map((post, index) => (
              <motion.article
                key={post.id}
                id={`post-${post.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.08 }}
                className="card-modern p-6"
              >
                {/* Header row: category badge + date + share */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-700 px-2 py-0.5">
                    {post.category}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(post.publish_at)}
                    </span>
                    <button
                      className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                      title="Copy link to this post"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}/wait-list#post-${post.id}`
                        );
                        toast.success('Link copied!');
                      }}
                    >
                      <FiLink2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Title */}
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {post.title}
                </h2>

                {/* Markdown body */}
                {post.content && (
                  <div className="prose-feed mb-4">
                    <ReactMarkdown>{post.content}</ReactMarkdown>
                  </div>
                )}

                {/* Optional external link */}
                {post.url && (
                  <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                    <a
                      href={`/go/${post.id}?src=web`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <FiExternalLink className="w-4 h-4" />
                      Visit Link
                    </a>
                  </div>
                )}
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default WaitList;
